import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Checkbox, CircularProgress, Fade, FormControlLabel, MenuItem, Modal, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCheck, faCircleCheck, faCopy, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { bonusApi, bonusErrorKey, referralApi } from '../utils/referralApi';
import { decodeTokenClaims } from '../utils/session';
import { popupBusy, releasePopup, takePopup } from '../utils/popupLock';
import { SUPPORTED_LANGUAGES } from '../translations';
import { LINKEDIN_TEMPLATES } from '../constants/linkedinBonusTemplates';
import LinkedInBonusSteps, { Glyph } from './LinkedInBonusSteps';
import './LinkedInBonus.css';

// LinkedIn bonus (ai-docs features/37-linkedin-bonus.md): 1,000 AI credits once for a LinkedIn post with the partner
// link in the first comment, plus the partner commission. Three steps: the offer, the post, the proof; "I've posted
// it" accepts the partner terms for whoever has not joined yet (Robert, 29.10.2026). Since 03.11.2026 it opens only
// on demand (Robert: not proactively for anyone any more): the sidebar's gold button (until the account has claimed
// it) and the partner page. AUTO_OPEN brings back the opening by itself: once per visit (a new tab or window, or a
// sign-in, is a new visit) for account owners with an agent (the backend decides "show"), wherever they land, the Wix
// dashboard included; never in a master-login session or during onboarding, and never on top of another self-opening
// popup (the Studio tour goes first, utils/popupLock). The X closes it; "Do not show again" ends that for good.

const AUTO_OPEN = false;
const VISIT_KEY = 'ub_bonus_visit';
const OPEN_EVENT = 'ub:open-linkedin-bonus';
export const CHANGED_EVENT = 'ub:linkedin-bonus-changed';
const QUIET_PATHS = ['/agents/new', '/wix', '/wordpress-register', '/webflow-register', '/create-chatbot', '/mcp', '/login', '/register'];
const DELAY_MS = 1500;
const POST = 1;
const PROOF = 2;
const DONE = 3;
const MARK = { gift: '§gift§', rate: '§rate§' };
const MENU = { MenuProps: { PaperProps: { sx: { borderRadius: '10px' } } } };

// A sign-in starts a new visit (App.js: login, sign-up, Google return), even in the same tab.
export function markBonusSignIn() {
  try { sessionStorage.removeItem(VISIT_KEY); } catch (e) { /* no storage */ }
}

// `from`: where it was opened, for the funnel ('partner_page', 'sidebar').
export function openLinkedInBonus(from) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { from: typeof from === 'string' ? from : 'partner_page' } }));
}

// The sidebar's gold button (BotUsageFooter, Robert 29.10.2026): the bonus credits while the account can claim them
// and has not yet; null otherwise, and after a claim is sent. Not in a master-login session.
export function useLinkedInBonusOffer() {
  const [credits, setCredits] = useState(null);
  useEffect(() => {
    if (neverHere()) return undefined;
    let alive = true;
    const take = (state) => { if (alive) setCredits(state?.eligible && !state.claim ? state.credits : null); };
    bonusApi('linkedin/state').then(take, () => {});
    const changed = (e) => take(e.detail);
    window.addEventListener(CHANGED_EVENT, changed);
    return () => { alive = false; window.removeEventListener(CHANGED_EVENT, changed); };
  }, []);
  return credits;
}

function track(event, meta) {
  bonusApi('linkedin/event', { method: 'POST', body: { event, meta } }).catch(() => {});
}

// Never in a master-login session. Embedded dashboards (Wix) show it too (Robert, 29.10.2026).
function neverHere() {
  return decodeTokenClaims(localStorage.getItem('token'))?.via === 'master';
}

// Inside the Wix dashboard's iframe the browser may refuse the Clipboard API (no clipboard-write permission);
// the old copy command still works there on a click.
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
    document.body.appendChild(area);
    area.select();
    let done = false;
    try { done = document.execCommand('copy'); } catch (err) { done = false; }
    area.remove();
    return done;
  }
}

// Not now: an onboarding or sign-in page; it waits until the user is somewhere else.
function quietNow() {
  return QUIET_PATHS.some((p) => window.location.pathname.startsWith(p));
}

function CopyButton({ text, onCopied }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  async function copy() {
    const done = await copyText(text);
    setCopied(done);
    if (done) onCopied?.();
    clearTimeout(timer.current); timer.current = setTimeout(() => setCopied(false), 2500);
  }
  return <button type="button" className={`lb-copy-btn${copied ? ' is-done' : ''}`} onClick={copy}>
    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />{t(copied ? 'bonus_copied' : 'bonus_copy')}
  </button>;
}

// The template's texts in a language: the post, and the comment's sentence before the link (the link stays fixed).
function templateTexts(lang) {
  const template = LINKEDIN_TEMPLATES[lang];
  return { post: template.post, lead: template.comment('').trim() };
}

// A text edited where it stands, as tall as its text (the post up to its CSS max-height, then it scrolls). Measured
// when the text changes, not with a ResizeObserver: that one looped with the body's own height.
function EditText({ value, onChange, label }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return <textarea ref={ref} className="lb-edit" aria-label={label} value={value} rows={1} spellCheck onChange={(e) => onChange(e.target.value)} />;
}

// Post or comment (Robert, 29.10.2026: the alignment has to be first class): a head (the initial, what it is, Copy)
// over a field of the full width. Both fields share their edges, their padding and where their text starts; a click
// on a field's padding puts the cursor in its text.
function Draft({ letter, label, copy, onCopied, post, children }) {
  const focus = (e) => {
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    e.currentTarget.querySelector('textarea')?.focus();
  };
  return <div className="lb-draft">
    <div className="lb-draft-head">
      <span className="lb-avatar"><Glyph text={letter} size={0.43} /></span>
      <span className="lb-draft-label">{label}</span>
      <CopyButton text={copy} onCopied={onCopied} />
    </div>
    <div className={`lb-editor${post ? ' is-post' : ''}`} onMouseDown={focus}>{children}</div>
  </div>;
}

function rateText(t, bps) {
  return t('partner_rate', { rate: new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(bps / 100) });
}

function BonusDialog({ initial, startStep, onClose, onNeverAgain }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState(initial);
  const [step, setStep] = useState(startStep);
  const [dir, setDir] = useState('fwd'); // the way the next step slides in
  const inner = useRef(null);
  const [height, setHeight] = useState(null); // the body follows its content smoothly from step to step
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The post's language follows the portal; the user can pick another one.
  const [lang, setLang] = useState(() => { const l = (i18n.language || 'en').slice(0, 2); return LINKEDIN_TEMPLATES[l] ? l : 'en'; });
  // Both texts can be edited right where they stand (Robert, 29.10.2026); another language starts from its template.
  const [texts, setTexts] = useState(() => templateTexts(lang));
  const [form, setForm] = useState({ profile_url: initial.claim?.profile_url || '', post_url: '', bot_id: initial.claim?.bot_id || initial.agents[0]?.id || '',
    confirm_profile: false, confirm_comment: false, confirm_public: false });
  const amount = state.credits;
  const credits = new Intl.NumberFormat(i18n.language || 'en').format(amount); // 1,000 / 1.000
  const deal = state.partner.joined ? state.partner.terms : state.partner.offer;
  const rate = rateText(t, deal.rate_bps);
  const letter = ((localStorage.getItem('first_name') || '').trim()[0] || 'U').toUpperCase(); // the profile picture's initial
  const link = state.link?.url || '';
  const comment = `${texts.lead.trim()} ${link}`.trim();
  const edited = (key) => texts[key] !== templateTexts(lang)[key];
  const termsUrl = `https://ultimo-bots.com${(i18n.language || '').startsWith('de') ? '/de' : ''}/referral/terms`;

  const publish = (next) => { setState(next); window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: next })); };
  const go = (next) => { setDir(next > step ? 'fwd' : 'back'); setError(''); setStep(next); };

  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  async function start() {
    track('started');
    setBusy(true); setError('');
    try {
      const { link } = await bonusApi('linkedin/link', { method: 'POST' });
      setState((s) => ({ ...s, link }));
      go(POST);
    } catch (e) { setError(bonusErrorKey(e)); } finally { setBusy(false); }
  }

  // "I've posted it": whoever is not a partner yet joins here, with the terms named right under the button.
  async function posted() {
    if (state.partner.joined) { go(PROOF); return; }
    setBusy(true); setError('');
    try {
      await referralApi('join', { method: 'POST', body: { accept_terms: true, terms_version: state.partner.terms_version } });
      track('terms_accepted');
      setState((s) => ({ ...s, partner: { ...s.partner, joined: true, status: 'active', terms: s.partner.offer } }));
      go(PROOF);
    } catch (e) { setError(bonusErrorKey(e)); } finally { setBusy(false); }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try { publish(await bonusApi('linkedin/claim', { method: 'POST', body: { ...form, language: lang } })); go(DONE); }
    catch (err) { setError(bonusErrorKey(err)); } finally { setBusy(false); }
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const ready = form.profile_url.trim() && form.post_url.trim() && form.bot_id && form.confirm_profile && form.confirm_comment && form.confirm_public;
  const gift = t('bonus_gift', { credits });
  const titles = [t('bonus_hero_title', { gift, rate }), t('bonus_post_title'), t('bonus_proof_title'), t('bonus_done_title')];
  // The headline with both gains in gold: "1,000 AI credits" as a whole, and the rate.
  const hero = t('bonus_hero_title', MARK).split(/(§gift§|§rate§)/).map((part, i) =>
    part === MARK.gift ? <span key={i} className="lb-hl">{gift}</span> : part === MARK.rate ? <span key={i} className="lb-hl">{rate}</span> : part);

  return <div className="lb-panel" role="dialog" aria-modal="true" aria-label={titles[step]}>
    <header className={`lb-head${step === 0 ? ' is-hero' : ''}`}>
      <div className="lb-grid" aria-hidden="true" />
      <button type="button" className="lb-close" onClick={() => onClose(step)} aria-label={t('close')}><FontAwesomeIcon icon={faXmark} /></button>
      <span className="lb-eyebrow"><FontAwesomeIcon icon={faLinkedin} /> {t('bonus_eyebrow')}</span>
      <div className="lb-title-row">
        {step > 0 && step < DONE && <button type="button" className="lb-back" onClick={() => go(step - 1)} aria-label={t('bonus_back')}><FontAwesomeIcon icon={faArrowLeft} /></button>}
        <h2 key={step} className="lb-title">{step === 0 ? hero : titles[step]}</h2>
      </div>
      {/* The line on the deal folds away smoothly on the next steps and opens again on the way back. */}
      <div className={`lb-hero${step === 0 ? '' : ' is-folded'}`}>
        <p className="lb-sub">{deal.duration_months ? t('bonus_hero_months', { months: deal.duration_months }) : t('bonus_hero_forever')}</p>
      </div>
      {step > 0 && step < DONE && <div className="lb-dots" aria-label={t('bonus_step', { n: step + 1, total: 3 })}>
        {[0, 1, 2].map((i) => <span key={i} className={`lb-dot${i === step ? ' is-on' : i < step ? ' is-done' : ''}`} />)}
        <span className="lb-step">{t('bonus_step', { n: step + 1, total: 3 })}</span>
      </div>}
    </header>

    {/* On the first page the body never shows a scrollbar: the scene's size follows the width, and a scrollbar that
        comes and goes would change that width every frame (the scene trembled, Robert 29.10.2026). */}
    <div className={`lb-body${step === 0 ? ' is-first' : ''}`} style={height != null ? { height } : undefined}>
     <div className="lb-body-inner" ref={inner}>
      <div key={step} className={`lb-step-in is-${dir}`}>
      {step === 0 && <LinkedInBonusSteps credits={credits} amount={amount} rate={rate} link={state.link?.url} />}

      {step === POST && <>
        {state.claim?.status === 'rejected' && <Alert severity="warning" className="lb-alert">{t('bonus_rejected_hint', { reason: state.claim.reject_reason })}</Alert>}
        {/* The template is a suggestion; any post that names Ultimo Bots, with the link in the first comment, counts. */}
        <p className="lb-note">{t('bonus_post_hint')}</p>
        <TextField size="small" select className="lb-lang" label={t('bonus_lang_label')} value={lang}
          onChange={(e) => { setLang(e.target.value); setTexts(templateTexts(e.target.value)); }} SelectProps={MENU}>
          {SUPPORTED_LANGUAGES.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
        </TextField>
        {/* The post and its first comment, each edited and copied where it stands; the link itself stays fixed. */}
        <Draft post letter={letter} label={t('bonus_post_label')} copy={texts.post} onCopied={() => track('copied_post', { language: lang, edited: edited('post') })}>
          <EditText label={t('bonus_post_label')} value={texts.post} onChange={(post) => setTexts((x) => ({ ...x, post }))} />
        </Draft>
        <Draft letter={letter} label={t('bonus_comment_label')} copy={comment} onCopied={() => track('copied_comment', { language: lang, edited: edited('lead') })}>
          <EditText label={t('bonus_comment_label')} value={texts.lead} onChange={(lead) => setTexts((x) => ({ ...x, lead }))} />
          <span className="lb-comment-link">{link}</span>
        </Draft>
      </>}

      {step === PROOF && <form id="lb-proof" className="lb-form" onSubmit={submit}>
        <TextField size="small" label={t('bonus_profile_label')} placeholder="linkedin.com/in/…" value={form.profile_url} onChange={set('profile_url')} autoFocus />
        <TextField size="small" label={t('bonus_postlink_label')} placeholder="linkedin.com/posts/…" value={form.post_url} onChange={set('post_url')} helperText={t('bonus_postlink_help')} />
        {state.agents.length > 1
          ? <TextField size="small" select label={t('bonus_agent_label')} value={form.bot_id} onChange={set('bot_id')} SelectProps={MENU}>
              {state.agents.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </TextField>
          : <p className="lb-note">{t('bonus_agent_single', { agent: state.agents[0]?.name })}</p>}
        <div className="lb-confirms">
          {['confirm_profile', 'confirm_comment', 'confirm_public'].map((k) => <FormControlLabel key={k} control={<Checkbox size="small" checked={form[k]} onChange={set(k)} />} label={t(`bonus_${k}`)} />)}
        </div>
      </form>}

      {step === DONE && <div className="lb-done">
        <FontAwesomeIcon icon={faCircleCheck} />
        <p>{t('bonus_done_text', { credits })}</p>
      </div>}

      </div>
      {error && <Alert severity="error" className="lb-alert">{t(error)}</Alert>}
     </div>
    </div>

    <footer className="lb-foot">
      <span className="lb-spacer" />
      {step === 0 && <>
        <button type="button" className="lb-secondary" onClick={onNeverAgain}>{t('bonus_never')}</button>
        <button type="button" className="lb-primary is-gold" disabled={busy} onClick={start}>{busy ? <CircularProgress size={16} color="inherit" /> : <>{t('bonus_start')} <FontAwesomeIcon icon={faArrowRight} /></>}</button>
      </>}
      {step === POST && <button type="button" className="lb-primary" disabled={busy || !state.link} onClick={posted}>{busy ? <CircularProgress size={16} color="inherit" /> : <>{t('bonus_posted')} <FontAwesomeIcon icon={faArrowRight} /></>}</button>}
      {step === PROOF && <button type="submit" form="lb-proof" className="lb-primary" disabled={busy || !ready}>{busy ? <CircularProgress size={16} color="inherit" /> : t('bonus_submit')}</button>}
      {step === DONE && <button type="button" className="lb-primary" onClick={() => onClose(DONE)}>{t('bonus_close')}</button>}
      {step === POST && !state.partner.joined && <p className="lb-foot-note">{t('bonus_posted_terms')}{' '}
        <a href={termsUrl} target="_blank" rel="noopener noreferrer">{t('bonus_posted_terms_link')}</a>.</p>}
    </footer>
  </div>;
}

// Closing shrinks the panel into the sidebar's gold button, which then pulses once, so the user sees where the
// bonus waits (Robert, 29.10.2026). Without that button on the screen (phone, no agent open, bonus claimed) or with
// reduced motion it just closes.
function flyToSidebar(panel, done) {
  const target = document.querySelector('.bot-usage-bonus');
  const to = target?.getBoundingClientRect();
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (!panel?.animate || !to?.width || to.right <= 0 || to.bottom > window.innerHeight || reduced) { done(); return false; }
  const from = panel.getBoundingClientRect();
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  const shrink = `translate(${dx}px, ${dy}px) scale(${to.width / from.width}, ${to.height / from.height})`;
  const flight = panel.animate([{ transform: 'none', opacity: 1 }, { opacity: 1, offset: 0.65 }, { transform: shrink, opacity: 0 }],
    { duration: 560, easing: 'cubic-bezier(0.5, 0, 0.25, 1)', fill: 'forwards' });
  flight.finished.then(() => {
    done();
    target.animate([{ transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 191, 46, 0.75)' },
      { transform: 'scale(1.06)', boxShadow: '0 0 0 10px rgba(255, 191, 46, 0)', offset: 0.45 }, { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 191, 46, 0)' }],
    { duration: 700, easing: 'ease-out' });
  }, done);
  return true;
}

export default function LinkedInBonus() {
  const [open, setOpen] = useState(null); // { state, step }
  const [flying, setFlying] = useState(false); // the panel is on its way into the sidebar
  const wrap = useRef(null);

  const show = useCallback((state, step) => { setFlying(false); setOpen({ state, step }); }, []);

  // With AUTO_OPEN: once per visit, 1.5 seconds after the first page (Robert, 29.10.2026); after the Studio tour if
  // that is open; later if the user is on an onboarding page right now.
  useEffect(() => {
    if (!AUTO_OPEN) return undefined;
    let seen = true;
    try { seen = sessionStorage.getItem(VISIT_KEY) === '1'; } catch (e) { seen = true; }
    if (seen || neverHere()) return undefined;
    let alive = true;
    let timer = null;
    const attempt = async () => {
      if (!alive) return;
      if (quietNow() || popupBusy('linkedin_bonus')) { timer = setTimeout(attempt, 1500); return; }
      try {
        const state = await bonusApi('linkedin/state');
        if (!alive || !state.show || quietNow() || !takePopup('linkedin_bonus')) return;
        try { sessionStorage.setItem(VISIT_KEY, '1'); } catch (e) { /* no storage: once per page load */ }
        show(state, 0);
        track('shown');
      } catch (e) { /* no modal */ }
    };
    timer = setTimeout(attempt, DELAY_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [show]);

  // On demand from the partner page or the sidebar: the offer, or the post again after a rejection.
  useEffect(() => {
    const handler = async (e) => {
      try {
        const state = await bonusApi('linkedin/state');
        if (!state.eligible) return;
        const again = state.claim?.status === 'rejected' && state.claim.resubmit_allowed;
        if (state.claim && !again) return;
        track('opened_from_page', { from: e.detail?.from || 'partner_page' });
        takePopup('linkedin_bonus');
        if (again) {
          const { link } = await bonusApi('linkedin/link', { method: 'POST' });
          show({ ...state, link }, POST);
        } else show(state, 0);
      } catch (e) { /* nothing to open */ }
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, [show]);

  const hide = () => {
    if (flying) return;
    const gone = () => { setOpen(null); releasePopup('linkedin_bonus'); }; // `flying` stays until the next opening: no flash of the backdrop
    if (flyToSidebar(wrap.current?.firstElementChild, gone)) setFlying(true);
  };
  const close = (step) => { if (step < DONE) track('closed', { step }); hide(); };
  const neverAgain = () => { hide(); bonusApi('linkedin/never-again', { method: 'POST' }).catch(() => {}); };
  useEffect(() => () => releasePopup('linkedin_bonus'), []);

  // A click beside the modal must not throw away a half-filled form; Escape and the X close it. While the panel flies
  // into the sidebar, the backdrop fades with it (.is-flying).
  return <Modal open={!!open} onClose={(_, reason) => reason !== 'backdropClick' && close(0)} closeAfterTransition
    className={`lb-modal${flying ? ' is-flying' : ''}`}>
    <Fade in={!!open} timeout={250}>
      <div className="lb-wrap" ref={wrap}>{open && <BonusDialog initial={open.state} startStep={open.step} onClose={close} onNeverAgain={neverAgain} />}</div>
    </Fade>
  </Modal>;
}
